import { useMutation, useQueryClient } from "@tanstack/react-query";
import { IReminder } from "../models/entities/reminder.model";
import { EndpointsService } from "../services/endpoints/endpoints.service";
import { MY_REMINDERS_QUERY_KEYS } from "./useMyReminders";

/**
 * Resolve a reminder — the SAME behaviour reminders have everywhere else
 * (Maria, KAN-2555: "same behaviour that reminders have in other places").
 *
 * Lifted verbatim from `ParticipantsReminders.tsx`'s `resolve()`: set
 * `resolved = true` and `updatedOn = now`, `PUT /api/reminders/` with the whole
 * row, one click, no confirm, no undo. The only difference is that the row
 * leaves the list because react-query refetches, rather than because the page
 * spliced its own state.
 *
 * `createdBy` is deliberately not sent — the existing update payload omits it.
 */
export const useResolveReminder = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (reminder: IReminder) =>
			await EndpointsService.dataRetriever.updateReminder({
				body: {
					id: reminder.id as number,
					kannactId: reminder.kannactId,
					patientId: reminder.patientId,
					reason: reminder.reason,
					dateOn: reminder.dateOn,
					resolved: true,
					createdOn: reminder.createdOn,
					updatedOn: new Date().toISOString(),
					assignedTo: reminder.assignedTo,
				},
			}),
		onSuccess: () =>
			MY_REMINDERS_QUERY_KEYS.forEach((key) =>
				queryClient.invalidateQueries({ queryKey: [key] })
			),
	});
};
